import dayjs from 'dayjs'
import { DateWindow } from './windows'
import { getCurrencySymbolForCode } from '../../../_utils/domain'

export const MINUS = '−'
export const NO_VALUE = '—'

// At 999,950 and above the K form would round to '1000.0K', so the M form takes over.
const ROUNDS_UP_TO_ONE_MILLION = 999_950

const COMPACT_FROM = 10_000

const withoutTrailingZeros = (decimal: string) => decimal.replace(/\.?0+$/, '')

export const formatCount = (n: number): string => {
    const magnitude = Math.abs(n)
    const sign = n < 0 ? MINUS : ''
    if (magnitude >= ROUNDS_UP_TO_ONE_MILLION)
        return `${sign}${withoutTrailingZeros((magnitude / 1_000_000).toFixed(2))}M`
    if (magnitude >= COMPACT_FROM) return `${sign}${(magnitude / 1_000).toFixed(1)}K`
    return `${sign}${magnitude.toLocaleString('en-US')}`
}

export const formatRate = (fraction: number | null, options: { purchaseRate?: boolean } = {}): string => {
    if (fraction === null || Number.isNaN(fraction)) return NO_VALUE
    const percent = fraction * 100
    const decimals = options.purchaseRate && Number(percent.toFixed(2)) < 10 ? 2 : 1
    return `${percent.toFixed(decimals)}%`
}

/** Below 10K a fractional amount is cents, so it shows exactly two decimals; larger amounts use the compact count form. */
const formatMoneyAmount = (amount: number): string => {
    const magnitude = Math.abs(amount)
    if (magnitude >= COMPACT_FROM || Number.isInteger(magnitude)) return formatCount(amount)
    const sign = amount < 0 ? MINUS : ''
    return `${sign}${magnitude.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

/** A count written out in full with thousands separators, for tables, where a figure is compared with its neighbours. */
export const formatCountInFull = (n: number): string =>
    `${n < 0 ? MINUS : ''}${Math.abs(n).toLocaleString('en-US', { maximumFractionDigits: 0 })}`

/** A domain's currency is unset when the account service names none; nothing is assumed in its place. */
export const NO_CURRENCY_NAME = 'no currency set'

/** The currencies as a sentence, naming an unset one as its own entry. */
export const currencyListFor = (currencyCodes: Array<string | undefined>): string =>
    joinedWithAnd(currencyCodes.map((currencyCode) => currencyCode ?? NO_CURRENCY_NAME))

/** Money is only written in a known currency: an unset one has no symbol to show, so it has no figure. */
export const formatMoney = (amount: number, currencyCode: string | undefined): string => {
    if (currencyCode === undefined) return NO_VALUE
    return `${getCurrencySymbolForCode(currencyCode) ?? ''}${formatMoneyAmount(amount)}`
}

/** Money written out in full: always two decimals, so a column of amounts lines up and no figure is abbreviated. */
export const formatMoneyInFull = (amount: number, currencyCode: string | undefined): string => {
    if (currencyCode === undefined) return NO_VALUE
    const body = Math.abs(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    return `${getCurrencySymbolForCode(currencyCode) ?? ''}${amount < 0 ? MINUS : ''}${body}`
}

/** The sign follows the rounded figure, so a tiny negative reads '+0.0' rather than a negative zero. */
const withSign = (value: number, decimals: number, unit: string) => {
    const body = Math.abs(value).toFixed(decimals)
    return value < 0 && Number(body) !== 0 ? `${MINUS}${body}${unit}` : `+${body}${unit}`
}

export const formatCountDelta = (deltaPct: number | null): string =>
    deltaPct === null ? NO_VALUE : withSign(deltaPct * 100, 1, '%')

export const formatRateDelta = (deltaPts: number | null): string =>
    deltaPts === null ? NO_VALUE : withSign(deltaPts, 1, ' pt')

const isSingleDay = (window: DateWindow) => window.since === window.through
const crossesYears = (window: DateWindow) => window.since.slice(0, 4) !== window.through.slice(0, 4)

/** A single day reads as one date, and a range that crosses a year boundary names both years. */
export const formatDateRange = (window: DateWindow): string => {
    if (isSingleDay(window)) return dayjs(window.since).format('MMM D, YYYY')
    if (crossesYears(window))
        return `${dayjs(window.since).format('MMM D, YYYY')} – ${dayjs(window.through).format('MMM D, YYYY')}`
    return `${dayjs(window.since).format('MMM D')} – ${dayjs(window.through).format('MMM D, YYYY')}`
}

/** The same without the year, for where the year is clear from the page; a range across years still names both. */
export const formatShortDateRange = (window: DateWindow): string => {
    if (crossesYears(window)) return formatDateRange(window)
    if (isSingleDay(window)) return dayjs(window.since).format('MMM D')
    return `${dayjs(window.since).format('MMM D')} – ${dayjs(window.through).format('MMM D')}`
}

/** "A", "A and B", "A, B and C". */
export const joinedWithAnd = (items: string[]): string =>
    items.length <= 1 ? items.join('') : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`
